import type { TableOrdering } from "@/components/BaseTable.vue"
import { useQuery } from "@tanstack/vue-query"
import type { MaybeRef } from "@vueuse/core"

export type Terminal = {
  id: string
  terminalId: string
  clientId: number | null
  clientName: string | null
  registrationDate: Date | null
  lastPaired: Date | null
  lastPairedVersion: string | null
  lastTransactedAt: Date | null
  isPaired: boolean
  awaitingManualConfirmation: boolean
}

const parseTerminal = (
  terminal: RecursivelyConvertDatesToStrings<Terminal>
): Terminal => ({
  ...terminal,
  registrationDate: terminal.registrationDate
    ? DateFns.parseISO(terminal.registrationDate)
    : null,
  lastPaired: terminal.lastPaired
    ? DateFns.parseISO(terminal.lastPaired)
    : null,
  lastTransactedAt: terminal.lastTransactedAt
    ? DateFns.parseISO(terminal.lastTransactedAt)
    : null,
})

export type ListTerminalsFilter = {
  search?: string
  merchantId?: number
}

export type ListTerminalsOrdering =
  | "terminalId"
  | "name"
  | "lastPaired"
  | "lastPairedVersion"
  | "registrationDate"

export type ListTerminalsQuery = ListTerminalsFilter & {
  ordering?: TableOrdering<ListTerminalsOrdering>
} & Pagination

export const listTerminals = async (
  query: ListTerminalsQuery
) => {
  const { body } = await api.get<
    Paginate<RecursivelyConvertDatesToStrings<Terminal[]>>
  >("/terminals/", {
    query: query,
  })

  return {
    ...body,
    items: body.items.map((terminal) =>
      parseTerminal(terminal)
    ),
  }
}

export const useTerminals = (
  query: MaybeRef<ListTerminalsQuery>,
  enabled: MaybeRef<boolean> = true
) =>
  useQuery({
    queryKey: [TERMINALS_QUERY_KEY, query] as const,
    enabled,
    queryFn: ({ queryKey: [_, query_] }) =>
      listTerminals(unref(query_)),
    keepPreviousData: true,
  })
